// Die Liste der Mandanten: Zustand, Version, Benutzer, Kosten im Monat.
// Die Zahlen sind die letzte Antwort der Instanz (`last_status`) — „Aktualisieren"
// fragt alle laufenden Instanzen neu.

import { useMutation } from "@tanstack/react-query";
import { post } from "../api/client";
import { useTenants } from "../api/queries";
import type { TenantListItem } from "../api/types";
import { PageHead } from "../app/page-head";
import { fmtDate, fmtInt, fmtUsd, monthCostUsd } from "../lib/format";
import { RUN_KIND, errorText, stateOf } from "../lib/labels";
import { Link, useNavigate } from "../router/router";
import { Badge, ErrorNote } from "../ui/badge";
import { Card } from "../ui/card";
import { toast } from "../ui/toaster";

/** Wohin eine Zeile führt: ein Entwurf zurück an seinen Schritt im Wizard. */
export function tenantHref(t: Pick<TenantListItem, "id" | "state" | "wizard_step">): string {
  return t.state === "draft" ? `/tenants/${t.id}/wizard/${Math.min(7, t.wizard_step || 1)}` : `/tenants/${t.id}`;
}

export function TenantsView() {
  const navigate = useNavigate();
  const query = useTenants();
  const refresh = useMutation({
    mutationFn: async () => {
      await post("/tenants/refresh");
      await query.refetch();
    },
    onError: (err) => toast(errorText(err), "error"),
  });
  const tenants = query.data;
  return (
    <>
      <PageHead
        title="Mandanten"
        hint={<>Jeder Mandant ist eine eigene Instanz mit eigener Datenbank unter <code>&lt;slug&gt;.solucoder.ai</code>.</>}
        actions={(
          <>
            <button type="button" className="pf-btn" disabled={refresh.isPending} onClick={() => refresh.mutate()}>Aktualisieren</button>
            <Link className="pf-btn pf-btn--primary" to="/tenants/new">Neuer Mandant</Link>
          </>
        )}
      />
      {query.isError && !tenants ? <ErrorNote error={query.error} /> : null}
      {tenants ? (
        <Card title={`${tenants.length} Mandanten`} flush>
          {tenants.length ? (
            <div className="pf-table-wrap">
              <table className="pf-table">
                <thead><tr><th>Mandant</th><th>Zustand</th><th className="pf-hide-phone">Version</th><th className="pf-num pf-hide-phone">Benutzer</th><th className="pf-num">Kosten (Monat)</th><th className="pf-hide-phone">Stand</th></tr></thead>
                <tbody>
                  {tenants.map((t) => {
                    const [st, tone] = stateOf(t.state);
                    const s = t.status ?? {};
                    const cost = t.cost ?? {};
                    const total = monthCostUsd(cost);
                    const href = tenantHref(t);
                    return (
                      <tr key={t.id} data-href={href} onClick={() => navigate(href)}>
                        <td className="pf-cell-main"><strong>{t.name || t.slug}</strong><div className="pf-sub pf-break">{t.host}</div></td>
                        <td>
                          <Badge tone={tone}>{st}</Badge>
                          {t.run?.state === "running" ? <> <span className="pf-sub">{RUN_KIND[t.run.kind] ?? t.run.kind} läuft</span></> : null}
                          {t.run?.state === "failed" ? <> <span className="pf-sub">letzter Lauf gescheitert</span></> : null}
                        </td>
                        <td className="pf-hide-phone">{s.version ?? "—"}<div className="pf-sub">{t.build ?? ""}</div></td>
                        <td className="pf-num pf-hide-phone">{fmtInt(s.users?.total)}<div className="pf-sub">{s.active_users_7d !== undefined ? `${fmtInt(s.active_users_7d)} aktiv` : ""}</div></td>
                        <td className="pf-num">{t.cost ? fmtUsd(total) : "—"}</td>
                        <td className="pf-hide-phone pf-sub">{fmtDate(t.status_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <div className="pf-empty">Noch kein Mandant. „Neuer Mandant“ führt durch die Einrichtung.</div>}
        </Card>
      ) : null}
    </>
  );
}
